/**
 * `/monitors/new` — stepper to create a monitor.
 *
 * Per `05-pages.md` §5c + `plans/monitors/04-dashboard.md` §4: an
 * internal stepper inside a single page (not a Dialog — easier to
 * deep-link from Origins / Page timeline "Monitor this site"/
 * "Monitor this page" CTAs). Three steps:
 *
 *   1. Target — pick an existing site (or pass via `?siteUuid=`).
 *   2. Schedule — daily / hourly. Hourly is plan-gated; a 402 from
 *      the API triggers `billingPrompt.open()` via the query cache
 *      error handler (Stage 4 wiring).
 *   3. Review + create.
 *
 * MIGRATION NOTE:
 *
 *   The legacy version of this stepper called `useUpdateMonitorSchedule`
 *   which PUT to `/v1/management/sites/{uuid}/settings` with
 *   `{ schedule, region_uuids, device_profile_uuids }`. That endpoint
 *   shape is gone (returns 410 ENDPOINT_GONE); monitor config moved
 *   to a first-class `monitors` table reachable via
 *   `POST /v1/management/monitors`. We now use `useCreateMonitor`.
 *
 *   The new endpoint requires `name`, `region_uuids` (≥1) and
 *   `device_profile_uuids` (≥1). The §04 plan calls for a full
 *   region + device-profile picker UI; for the minimal-fix
 *   migration we:
 *
 *     - Default `region_uuids` to every enabled region (`GET /v1/regions`).
 *     - Default `device_profile_uuids` to every device profile attached
 *       to the chosen site (`GET /v1/sites/:uuid/device-profiles`).
 *     - Auto-derive `name` from `${site.name} — ${cadence}`. Users
 *       can edit later via the (forthcoming) `$monitorUuid.edit`
 *       route. If the name collides on the site, the API surfaces
 *       `MONITOR_NAME_CONFLICT` (422) and we suffix with a
 *       timestamp on retry — minimal fix UX, refined in §04.
 *
 *   Editing an existing monitor's schedule is OUT OF SCOPE here; the
 *   `useEffect` that pre-filled the schedule from `useSiteSettings`
 *   was removed because `site_settings.schedule` no longer exists.
 *   The §04 rewrite adds `$monitorUuid.edit.tsx` for that.
 */
import { PageActions } from '@/components/primitives/PageActions';
import { UpsellInline } from '@/components/primitives/UpsellInline';
import { Island } from '@/components/shell/Island';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { FetchError } from '@/lib/api';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { hostOf } from '@/lib/format';
import {
  type DeviceProfileSummary,
  type Region,
  useCreateMonitor,
  useEntitlement,
  useOrgDeviceProfiles,
  useOrigins,
  useRegions,
} from '@/lib/queries';
import { cn } from '@/lib/utils';
import { ArrowLeft, ArrowRight, Check } from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { type ReactElement, useEffect, useMemo, useState } from 'react';

type NewMonitorSearch = { siteUuid?: string; pageUuid?: string };

export const Route = createFileRoute('/_app/monitors/new')({
  component: NewMonitorPage,
  validateSearch: (raw: Record<string, unknown>): NewMonitorSearch => {
    const out: NewMonitorSearch = {};
    if (typeof raw.siteUuid === 'string' && raw.siteUuid.length > 0) out.siteUuid = raw.siteUuid;
    if (typeof raw.pageUuid === 'string' && raw.pageUuid.length > 0) out.pageUuid = raw.pageUuid;
    return out;
  },
});

type Schedule = 'daily' | 'hourly';

// Canonical cron strings the API accepts. Kept inline here (there is
// no shared catalogue today); `classifyCadence` on the server side
// recognises both bucket shapes.
const CRON_FOR: Record<Schedule, string> = {
  daily: '0 9 * * *', // 09:00 UTC
  hourly: '0 * * * *',
};

const STEPS = [
  { id: 'target', label: 'Target' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'coverage', label: 'Coverage' },
  { id: 'review', label: 'Review' },
] as const;

function NewMonitorPage(): ReactElement {
  const { siteUuid: prefilledSite, pageUuid: prefilledPage } = useSearch({
    from: '/_app/monitors/new',
  });
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [siteUuid, setSiteUuid] = useState<string | undefined>(prefilledSite);
  const [schedule, setSchedule] = useState<Schedule>('daily');
  // Coverage selections — explicit, user-driven. We never silently
  // auto-apply "all available". The picker pre-fills with a
  // conservative default (one region + one stock device profile)
  // when the catalogue first resolves, but the user must affirm it
  // before advancing past Step 3 ("Coverage"). Empty selection is
  // a hard error — no monitor without explicit coverage.
  const [selectedRegionUuids, setSelectedRegionUuids] = useState<string[]>([]);
  const [selectedDeviceProfileUuids, setSelectedDeviceProfileUuids] = useState<string[]>([]);
  const [defaultsApplied, setDefaultsApplied] = useState({ regions: false, devices: false });
  const [formError, setFormError] = useState<string | undefined>(undefined);

  const origins = useOrigins({ limit: 200 });
  const regions = useRegions();
  // Catalogue listing — stock + the org's named profiles. The
  // previous site-scoped hook only returned profiles ATTACHED to
  // the site via `device_profile_entities`, so a fresh site got
  // `[]` and the stepper stalled on "0 device profiles". Using the
  // org-wide listing means the picker always has the platform's
  // stock pool to draw on.
  const deviceProfiles = useOrgDeviceProfiles();
  const create = useCreateMonitor();
  // Plan-tier gate for the "hourly" schedule option. Daily runs on
  // every paid tier; hourly requires Pro+. Suspended orgs are
  // blocked one rung up (can't create a monitor at all) — the
  // create CTA on the index page handles that.
  const hourlyEntitlement = useEntitlement('monitor_hourly');

  const selectedSite = useMemo(
    () => origins.data?.sites.find((s) => s.uuid === siteUuid),
    [origins.data, siteUuid],
  );

  // Seed the picker with a conservative default the first time the
  // catalogue resolves. Once we've stamped the default we stop
  // overwriting the user's choice — even if the catalogue refreshes
  // in the background. `defaultsApplied` flips per-axis so a slow
  // device-profiles fetch doesn't block region defaulting.
  //
  // Region default: the first enabled region. Sorted by `gcp_region`
  // alphabetically by the DAO so this is stable.
  //
  // Device profile default: prefer `mobile/4g` (Lighthouse mobile
  // baseline), else first stock row, else first row overall.
  // Picking just one keeps "create monitor" cheap by default; users
  // who want desktop or multiple form factors tick the extra boxes.
  const STOCK_DEFAULT_KEY = 'mobile/4g';
  useEffect(() => {
    if (defaultsApplied.regions) return;
    const enabled = (regions.data ?? []).filter((r) => r.enabled);
    if (enabled.length === 0) return;
    const first = enabled[0];
    if (!first) return;
    setSelectedRegionUuids([first.uuid]);
    setDefaultsApplied((prev) => ({ ...prev, regions: true }));
  }, [regions.data, defaultsApplied.regions]);
  useEffect(() => {
    if (defaultsApplied.devices) return;
    const all = deviceProfiles.data ?? [];
    if (all.length === 0) return;
    const preferred =
      all.find((d) => d.source === 'stock' && d.preset_key === STOCK_DEFAULT_KEY) ??
      all.find((d) => d.source === 'stock') ??
      all[0];
    if (!preferred) return;
    setSelectedDeviceProfileUuids([preferred.uuid]);
    setDefaultsApplied((prev) => ({ ...prev, devices: true }));
  }, [deviceProfiles.data, defaultsApplied.devices]);

  function next() {
    setFormError(undefined);
    if (step === 0 && !siteUuid) {
      setFormError('Pick a site first.');
      return;
    }
    if (step === 2) {
      if (selectedRegionUuids.length === 0) {
        setFormError('Pick at least one region for the monitor to run in.');
        return;
      }
      if (selectedDeviceProfileUuids.length === 0) {
        setFormError('Pick at least one device profile.');
        return;
      }
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  }

  function back() {
    setFormError(undefined);
    setStep((s) => Math.max(0, s - 1));
  }

  async function createMonitor() {
    if (!siteUuid || !selectedSite) {
      setFormError('Pick a site first.');
      return;
    }
    if (selectedRegionUuids.length === 0) {
      setFormError('Pick at least one region for the monitor to run in.');
      return;
    }
    if (selectedDeviceProfileUuids.length === 0) {
      setFormError('Pick at least one device profile.');
      return;
    }
    setFormError(undefined);
    try {
      const monitor = await create.mutateAsync({
        body: {
          site_uuid: siteUuid,
          name: defaultMonitorName(selectedSite.name, schedule),
          schedule: CRON_FOR[schedule],
          region_uuids: selectedRegionUuids,
          device_profile_uuids: selectedDeviceProfileUuids,
          // Scope monitor to a single page when the stepper was
          // entered from the page-timeline "Monitor this page" CTA.
          // Otherwise null = monitor every enabled page on the site.
          page_uuids: prefilledPage ? [prefilledPage] : null,
        },
      });
      toastSuccess('Monitor created', `${selectedSite.name} now runs ${schedule}.`);
      // §04 lands `/monitors/$monitorUuid` — when that exists we'll
      // route there. For now, return to the index where the new
      // monitor appears in the list.
      void monitor;
      navigate({ to: '/monitors' });
    } catch (err) {
      // QueryCache handler already surfaced a toast (incl. 402 →
      // billing prompt). Keep the inline context for the form.
      if (err instanceof FetchError) setFormError(err.message);
      else if (err instanceof Error) setFormError(err.message);
      else toastFromError(err);
    }
  }

  return (
    <Island
      breadcrumb={[{ label: 'Monitors', href: '/monitors' }, { label: 'New' }]}
      actions={
        <PageActions>
          <Button variant="ghost" render={<Link to="/monitors" />}>
            Cancel
          </Button>
        </PageActions>
      }
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <Stepper active={step} />

        {step === 0 && (
          <StepTarget
            origins={origins.data?.sites ?? []}
            loading={origins.isLoading}
            value={siteUuid}
            onChange={setSiteUuid}
          />
        )}
        {step === 1 && (
          <StepSchedule
            value={schedule}
            onChange={setSchedule}
            hourlyAllowed={hourlyEntitlement.allowed}
            hourlyMinPlan={hourlyEntitlement.minPlan}
          />
        )}
        {step === 2 && (
          <StepCoverage
            regions={regions.data ?? []}
            regionsLoading={regions.isLoading}
            selectedRegionUuids={selectedRegionUuids}
            onRegionsChange={setSelectedRegionUuids}
            deviceProfiles={deviceProfiles.data ?? []}
            deviceProfilesLoading={deviceProfiles.isLoading}
            selectedDeviceProfileUuids={selectedDeviceProfileUuids}
            onDeviceProfilesChange={setSelectedDeviceProfileUuids}
          />
        )}
        {step === 3 && (
          <StepReview
            siteName={selectedSite?.name ?? '—'}
            siteUrl={selectedSite?.url ?? ''}
            schedule={schedule}
            selectedRegions={(regions.data ?? []).filter((r) =>
              selectedRegionUuids.includes(r.uuid),
            )}
            selectedDeviceProfiles={(deviceProfiles.data ?? []).filter((d) =>
              selectedDeviceProfileUuids.includes(d.uuid),
            )}
          />
        )}

        {/*
          Form-level error surface. Wrapped in a `<Field>` because
          Base UI's `<Field.Error>` reads from `FieldRootContext` and
          throws Base UI #28 ("must be inside a Field.Root") when
          rendered standalone. Without this wrapper the whole route
          unmounts the moment `formError` becomes truthy — which
          blew up the create flow when the API started returning 410
          on the legacy endpoint.
        */}
        {formError && (
          <Field>
            <FieldError match>{formError}</FieldError>
          </Field>
        )}

        <footer className="flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0}>
            <ArrowLeft aria-hidden="true" />
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next}>
              Continue
              <ArrowRight aria-hidden="true" />
            </Button>
          ) : (
            <Button onClick={createMonitor} loading={create.isPending}>
              <Check aria-hidden="true" />
              Create monitor
            </Button>
          )}
        </footer>
      </div>
    </Island>
  );
}

/**
 * Default monitor name: `<site name> — <cadence>`. Server enforces
 * `unique(site_id, name)`; if it collides we surface
 * `MONITOR_NAME_CONFLICT` to the form and the user can retry from
 * the §04 edit screen. This is a minimal-fix concession; the §04
 * stepper exposes the name field directly.
 */
function defaultMonitorName(siteName: string, schedule: Schedule): string {
  const cadenceLabel = schedule === 'hourly' ? 'Hourly' : 'Daily';
  // Clamp to the API's 120-char limit.
  return `${siteName} — ${cadenceLabel}`.slice(0, 120);
}

// -----------------------------------------------------------------
// Stepper chrome
// -----------------------------------------------------------------

function Stepper({ active }: { active: number }): ReactElement {
  return (
    <ol className="flex items-center gap-2 text-sm">
      {STEPS.map((s, i) => {
        const done = i < active;
        const current = i === active;
        return (
          <li key={s.id} className="flex items-center gap-2">
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full text-xs font-semibold',
                done
                  ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]'
                  : current
                    ? 'bg-[var(--color-accent-brand)] text-white'
                    : 'bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)]',
              )}
              aria-current={current ? 'step' : undefined}
            >
              {done ? <Check size={12} weight="bold" /> : i + 1}
            </span>
            <span
              className={cn(
                current ? 'font-medium text-[var(--color-ink)]' : 'text-[var(--color-mute)]',
              )}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <span aria-hidden="true" className="mx-2 h-px w-6 bg-[var(--color-line)]" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// -----------------------------------------------------------------
// Steps
// -----------------------------------------------------------------

function StepTarget({
  origins,
  loading,
  value,
  onChange,
}: {
  origins: { uuid: string; name: string; url: string }[];
  loading: boolean;
  value: string | undefined;
  onChange: (uuid: string) => void;
}): ReactElement {
  if (loading) return <Skeleton className="h-32 w-full" />;
  return (
    <section className="space-y-4 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
      <header>
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Pick a site to monitor</h2>
        <p className="text-sm text-[var(--color-mute)]">
          Choose from the sites you've already added. New sites? Head to{' '}
          <Link to="/origins" className="underline">
            Origins
          </Link>{' '}
          first.
        </p>
      </header>
      <Field>
        <FieldLabel htmlFor="monitor-site">Site</FieldLabel>
        <Select
          value={value}
          onValueChange={(v) => {
            if (typeof v === 'string') onChange(v);
          }}
        >
          <SelectTrigger id="monitor-site">
            <SelectValue placeholder="Select a site" />
          </SelectTrigger>
          <SelectContent>
            {origins.map((s) => (
              <SelectItem key={s.uuid} value={s.uuid}>
                {s.name} — {hostOf(s.url)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </section>
  );
}

function StepSchedule({
  value,
  onChange,
  hourlyAllowed,
  hourlyMinPlan,
}: {
  value: Schedule;
  onChange: (next: Schedule) => void;
  hourlyAllowed: boolean;
  hourlyMinPlan: string;
}): ReactElement {
  const options: Array<{ id: Schedule; label: string; description: string; gated?: boolean }> = [
    {
      id: 'daily',
      label: 'Daily',
      description: 'Runs once a day at 09:00 UTC. Enough for most marketing sites.',
    },
    {
      id: 'hourly',
      label: 'Hourly',
      description: 'Runs every hour. Usually reserved for production apps.',
      gated: !hourlyAllowed,
    },
  ];
  return (
    <section className="space-y-4 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
      <header>
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Schedule</h2>
        <p className="text-sm text-[var(--color-mute)]">How often should we run this audit?</p>
      </header>
      <Field>
        <fieldset className="space-y-2">
          <legend className="sr-only">Schedule frequency</legend>
          {options.map((o) => {
            const active = o.id === value;
            const locked = o.id === 'hourly' && !hourlyAllowed;
            return (
              <label
                key={o.id}
                className={cn(
                  'flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
                  active
                    ? 'border-[var(--color-accent-brand)] bg-[var(--color-accent-brand)]/5'
                    : 'border-[var(--color-line)] hover:bg-[var(--color-paper-alt)]/40',
                  locked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                )}
                aria-disabled={locked || undefined}
              >
                <input
                  type="radio"
                  name="schedule"
                  value={o.id}
                  checked={active}
                  onChange={() => {
                    if (locked) return;
                    onChange(o.id);
                  }}
                  disabled={locked}
                  className="mt-1 size-4 accent-[var(--color-accent-brand)]"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[var(--color-ink)]">{o.label}</span>
                    {locked && <UpsellInline variant="badge" minPlan={hourlyMinPlan} />}
                  </div>
                  <p className="text-xs text-[var(--color-mute)]">{o.description}</p>
                </div>
              </label>
            );
          })}
        </fieldset>
      </Field>
    </section>
  );
}

function StepCoverage({
  regions,
  regionsLoading,
  selectedRegionUuids,
  onRegionsChange,
  deviceProfiles,
  deviceProfilesLoading,
  selectedDeviceProfileUuids,
  onDeviceProfilesChange,
}: {
  regions: Region[];
  regionsLoading: boolean;
  selectedRegionUuids: string[];
  onRegionsChange: (next: string[]) => void;
  deviceProfiles: DeviceProfileSummary[];
  deviceProfilesLoading: boolean;
  selectedDeviceProfileUuids: string[];
  onDeviceProfilesChange: (next: string[]) => void;
}): ReactElement {
  // Toggling logic — multi-select, no minimum enforced at this layer
  // (the parent's `next()` validates ≥1 of each before advancing).
  const toggleRegion = (uuid: string) =>
    onRegionsChange(
      selectedRegionUuids.includes(uuid)
        ? selectedRegionUuids.filter((u) => u !== uuid)
        : [...selectedRegionUuids, uuid],
    );
  const toggleDeviceProfile = (uuid: string) =>
    onDeviceProfilesChange(
      selectedDeviceProfileUuids.includes(uuid)
        ? selectedDeviceProfileUuids.filter((u) => u !== uuid)
        : [...selectedDeviceProfileUuids, uuid],
    );

  // Group device profiles by source so the picker reads as
  // "Stock presets" + "Your profiles" rather than an indiscriminate
  // list. Stock comes first (the most-used path); named/auto rows
  // appear under their own header only when present.
  const stock = deviceProfiles.filter((d) => d.source === 'stock');
  const custom = deviceProfiles.filter((d) => d.source !== 'stock');
  const enabledRegions = regions.filter((r) => r.enabled);

  return (
    <section className="space-y-6 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
      <header className="space-y-1">
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Coverage</h2>
        <p className="text-sm text-[var(--color-mute)]">
          Pick the regions and device profiles each scheduled run should cover. Each combination
          produces one audit per run, so picking 2 regions × 2 profiles = 4 audits per cron tick.
        </p>
      </header>

      {/* ── Regions ───────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-sm font-medium text-[var(--color-ink)]">
            Regions{' '}
            <span className="text-[var(--color-mute)] font-normal">
              ({selectedRegionUuids.length} selected)
            </span>
          </h3>
          <SelectAllButtons
            allCount={enabledRegions.length}
            selectedCount={selectedRegionUuids.length}
            onSelectAll={() => onRegionsChange(enabledRegions.map((r) => r.uuid))}
            onClear={() => onRegionsChange([])}
          />
        </div>
        {regionsLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : enabledRegions.length === 0 ? (
          <p className="text-sm text-[var(--color-mute)]">
            No regions are available right now. Try again in a moment.
          </p>
        ) : (
          <ul className="space-y-2">
            {enabledRegions.map((r) => {
              const checked = selectedRegionUuids.includes(r.uuid);
              return (
                <li key={r.uuid}>
                  <div
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
                      checked
                        ? 'border-[var(--color-accent-brand)] bg-[var(--color-accent-brand)]/5'
                        : 'border-[var(--color-line)] hover:bg-[var(--color-paper-alt)]/40',
                    )}
                    onClick={() => toggleRegion(r.uuid)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleRegion(r.uuid);
                      }
                    }}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={0}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleRegion(r.uuid)}
                      className="mt-0.5"
                      aria-label={r.name}
                      // Stop propagation so clicking the checkbox itself
                      // doesn't fire the row's onClick + toggle twice.
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[var(--color-ink)]">{r.name}</div>
                      <div className="text-xs text-[var(--color-mute)]">
                        {r.location} · {r.gcp_region}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Device profiles ───────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-sm font-medium text-[var(--color-ink)]">
            Device profiles{' '}
            <span className="text-[var(--color-mute)] font-normal">
              ({selectedDeviceProfileUuids.length} selected)
            </span>
          </h3>
          <SelectAllButtons
            allCount={deviceProfiles.length}
            selectedCount={selectedDeviceProfileUuids.length}
            onSelectAll={() => onDeviceProfilesChange(deviceProfiles.map((d) => d.uuid))}
            onClear={() => onDeviceProfilesChange([])}
          />
        </div>
        {deviceProfilesLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : deviceProfiles.length === 0 ? (
          <p className="text-sm text-[var(--color-mute)]">
            No device profiles are available right now. Try again in a moment.
          </p>
        ) : (
          <div className="space-y-4">
            {stock.length > 0 && (
              <DeviceProfileGroup
                title="Stock presets"
                hint="Lighthouse-standard device + network combinations."
                profiles={stock}
                selectedUuids={selectedDeviceProfileUuids}
                onToggle={toggleDeviceProfile}
              />
            )}
            {custom.length > 0 && (
              <DeviceProfileGroup
                title="Your profiles"
                hint="Custom profiles configured for this organisation."
                profiles={custom}
                selectedUuids={selectedDeviceProfileUuids}
                onToggle={toggleDeviceProfile}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function DeviceProfileGroup({
  title,
  hint,
  profiles,
  selectedUuids,
  onToggle,
}: {
  title: string;
  hint: string;
  profiles: DeviceProfileSummary[];
  selectedUuids: string[];
  onToggle: (uuid: string) => void;
}): ReactElement {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-mute)]">
          {title}
        </div>
        <div className="text-xs text-[var(--color-mute)]">{hint}</div>
      </div>
      <ul className="space-y-2">
        {profiles.map((d) => {
          const checked = selectedUuids.includes(d.uuid);
          return (
            <li key={d.uuid}>
              <div
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
                  checked
                    ? 'border-[var(--color-accent-brand)] bg-[var(--color-accent-brand)]/5'
                    : 'border-[var(--color-line)] hover:bg-[var(--color-paper-alt)]/40',
                )}
                onClick={() => onToggle(d.uuid)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggle(d.uuid);
                  }
                }}
                role="checkbox"
                aria-checked={checked}
                tabIndex={0}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggle(d.uuid)}
                  className="mt-0.5"
                  aria-label={d.description}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-[var(--color-ink)]">
                      {d.description}
                    </span>
                    {d.device_type && (
                      <Badge variant="outline" size="sm">
                        {d.device_type}
                      </Badge>
                    )}
                    {d.preset_key && (
                      <Badge variant="secondary" size="sm">
                        {d.preset_key}
                      </Badge>
                    )}
                  </div>
                  {(d.viewport_width !== null && d.viewport_height !== null) ||
                  d.locale !== 'en-US' ? (
                    <div className="mt-1 text-xs text-[var(--color-mute)]">
                      {d.viewport_width !== null && d.viewport_height !== null && (
                        <span>
                          {d.viewport_width}×{d.viewport_height}
                          {d.device_scale_factor !== null && d.device_scale_factor !== 1
                            ? ` @${d.device_scale_factor}×`
                            : ''}
                        </span>
                      )}
                      {d.locale !== 'en-US' && <span className="ml-2">locale: {d.locale}</span>}
                    </div>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SelectAllButtons({
  allCount,
  selectedCount,
  onSelectAll,
  onClear,
}: {
  allCount: number;
  selectedCount: number;
  onSelectAll: () => void;
  onClear: () => void;
}): ReactElement {
  return (
    <div className="flex items-center gap-2 text-xs">
      <button
        type="button"
        onClick={onSelectAll}
        disabled={selectedCount === allCount || allCount === 0}
        className="text-[var(--color-accent-brand)] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
      >
        Select all
      </button>
      <span className="text-[var(--color-line)]">·</span>
      <button
        type="button"
        onClick={onClear}
        disabled={selectedCount === 0}
        className="text-[var(--color-mute)] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
      >
        Clear
      </button>
    </div>
  );
}

function StepReview({
  siteName,
  siteUrl,
  schedule,
  selectedRegions,
  selectedDeviceProfiles,
}: {
  siteName: string;
  siteUrl: string;
  schedule: Schedule;
  selectedRegions: Region[];
  selectedDeviceProfiles: DeviceProfileSummary[];
}): ReactElement {
  // Combination count is the per-run audit fan-out — runs × regions ×
  // device profiles. Surfacing it on Review lets the user spot a
  // misconfigured "all stock × all regions" before committing to it.
  const combinations = selectedRegions.length * selectedDeviceProfiles.length;
  return (
    <section className="space-y-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
      <header>
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Review</h2>
        <p className="text-sm text-[var(--color-mute)]">Double-check and create your monitor.</p>
      </header>
      <dl className="space-y-2 text-sm">
        <Row label="Site" value={siteName} sub={siteUrl} />
        <Row
          label="Schedule"
          value={schedule === 'daily' ? 'Daily at 09:00 UTC' : 'Every hour'}
          sub={`cron: ${CRON_FOR[schedule]}`}
        />
        <Row
          label={`Regions (${selectedRegions.length})`}
          value={selectedRegions.map((r) => r.name).join(', ') || '—'}
        />
        <Row
          label={`Device profiles (${selectedDeviceProfiles.length})`}
          value={selectedDeviceProfiles.map((d) => d.description).join(', ') || '—'}
        />
        <Row
          label="Audits per run"
          value={`${combinations}`}
          sub={`${selectedRegions.length} ${selectedRegions.length === 1 ? 'region' : 'regions'} × ${selectedDeviceProfiles.length} ${selectedDeviceProfiles.length === 1 ? 'profile' : 'profiles'}`}
        />
      </dl>
    </section>
  );
}

function Row({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[var(--color-line-soft)] pb-2 last:border-0 last:pb-0">
      <dt className="text-[var(--color-mute)]">{label}</dt>
      <dd className="text-right">
        <div className="font-medium text-[var(--color-ink)]">{value}</div>
        {sub && <div className="text-xs text-[var(--color-mute)]">{sub}</div>}
      </dd>
    </div>
  );
}
